<template>
  <div class="article-page">
    <!-- Ambient glowing backgrounds -->
    <div class="article-page__ambient-glow"></div>
    <div class="article-page__ambient-glow article-page__ambient-glow--alt"></div>

    <div v-if="article" class="article-page__wrapper">
      <!-- Animated border layer -->
      <div class="article-page__animated-border"></div>

      <!-- Main glass panel -->
      <div class="article-page__glass-panel">
        <!-- Breadcrumb -->
        <nav class="article-page__breadcrumb" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <span class="separator">/</span>
          <a :href="`/${article.category}/`">{{ article.category }}</a>
          <span class="separator">/</span>
          <span class="current">{{ article.title }}</span>
        </nav>

        <!-- Title -->
        <h1 class="article-page__title">{{ article.title }}</h1>

        <!-- Meta bar -->
        <div class="article-page__meta-bar">
          <span class="article-page__author">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            {{ article.author }}
          </span>
          <span class="article-page__date">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {{ formatDate(article.publishDate) }}
          </span>
          <span v-if="article.category" class="article-page__category-badge">
            {{ article.category }}
          </span>
        </div>

        <div class="article-page__share-bar">
          <span class="article-page__share-label">Share</span>
          <button type="button" class="article-page__share-button" @click="shareArticle('copy')">
            Copy Link
          </button>
          <button type="button" class="article-page__share-button" @click="shareArticle('x')">
            X
          </button>
          <button type="button" class="article-page__share-button" @click="shareArticle('facebook')">
            Facebook
          </button>
        </div>

        <!-- Tags -->
        <div v-if="article.tags && article.tags.length" class="article-page__tags">
          <span v-for="tag in article.tags" :key="tag" class="article-page__tag">{{ tag }}</span>
        </div>

        <!-- Article content -->
        <ArticleContent
          class="article-page__content"
          :content="article.content"
        />
      </div>

      <!-- Related Articles by Tags -->
      <section v-if="relatedByTags.length" class="article-page__related-section">
        <h2 class="article-page__section-title">Related Articles</h2>
        <div class="article-page__cards-grid">
          <a
            v-for="item in relatedByTags"
            :key="item.slug"
            :href="`/${item.category}/${item.slug}/`"
            class="article-page__card"
          >
            <div class="article-page__card-image" v-if="item.featuredImage">
              <img :src="item.featuredImage" :alt="item.title" loading="lazy" />
            </div>
            <div class="article-page__card-body">
              <h3 class="article-page__card-title">{{ item.title }}</h3>
              <span class="article-page__card-date">{{ formatDate(item.publishDate) }}</span>
            </div>
          </a>
        </div>
      </section>

      <!-- Related Articles by Category -->
      <section v-if="relatedByCategory.length" class="article-page__related-section">
        <h2 class="article-page__section-title">You May Also Like</h2>
        <div class="article-page__cards-grid">
          <a
            v-for="item in relatedByCategory"
            :key="item.slug"
            :href="`/${item.category}/${item.slug}/`"
            class="article-page__card"
          >
            <div class="article-page__card-image" v-if="item.featuredImage">
              <img :src="item.featuredImage" :alt="item.title" loading="lazy" />
            </div>
            <div class="article-page__card-body">
              <h3 class="article-page__card-title">{{ item.title }}</h3>
              <span class="article-page__card-date">{{ formatDate(item.publishDate) }}</span>
            </div>
          </a>
        </div>
      </section>

      <!-- Latest Posts -->
      <section v-if="latestArticles.length" class="article-page__related-section">
        <h2 class="article-page__section-title">Latest Posts</h2>
        <div class="article-page__cards-grid">
          <a
            v-for="item in latestArticles"
            :key="item.slug"
            :href="`/${item.category}/${item.slug}/`"
            class="article-page__card"
          >
            <div class="article-page__card-image" v-if="item.featuredImage">
              <img :src="item.featuredImage" :alt="item.title" loading="lazy" />
            </div>
            <div class="article-page__card-body">
              <h3 class="article-page__card-title">{{ item.title }}</h3>
              <span class="article-page__card-date">{{ formatDate(item.publishDate) }}</span>
            </div>
          </a>
        </div>
      </section>

      <!-- Prev/Next Navigation -->
      <nav v-if="prevNextArticle.prev || prevNextArticle.next" class="article-page__prev-next">
        <a
          v-if="prevNextArticle.prev"
          :href="`/${prevNextArticle.prev.category}/${prevNextArticle.prev.slug}/`"
          class="article-page__nav-link article-page__nav-link--prev"
        >
          <span class="article-page__nav-label">← Previous Post</span>
          <span class="article-page__nav-title">{{ prevNextArticle.prev.title }}</span>
        </a>
        <a
          v-if="prevNextArticle.next"
          :href="`/${prevNextArticle.next.category}/${prevNextArticle.next.slug}/`"
          class="article-page__nav-link article-page__nav-link--next"
        >
          <span class="article-page__nav-label">Next Post →</span>
          <span class="article-page__nav-title">{{ prevNextArticle.next.title }}</span>
        </a>
      </nav>
    </div>

    <!-- Not found state -->
    <div v-else class="article-page__not-found">
      <div class="article-page__not-found-glow"></div>
      <div class="article-page__not-found-panel">
        <h1>Article Not Found</h1>
        <p>The article you're looking for doesn't exist or has been removed.</p>
        <router-link to="/" class="article-page__back-link">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Home
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import ArticleContent from '@/components/article/ArticleContent.vue'
import { generateArticleMetaInfo, buildBreadcrumbs } from '@/utils/seo'

export default {
  name: 'ArticlePage',
  components: {
    ArticleContent,
  },
  async asyncData({ store, route, context }) {
    const category = route.meta.category
    const slug = route.params.slug
    const article = await store.dispatch('articles/fetchArticleBySlug', { category, slug })
    if (!article && context) {
      context.code = 404
    }
    // Load all articles for related content and navigation
    await store.dispatch('articles/loadAllArticles')
  },
  computed: {
    article() {
      return this.$store.state.articles.currentArticle
    },
    relatedByTags() {
      if (!this.article || !this.article.tags) return []
      return this.$store.getters['articles/getRelatedByTags'](this.article.tags, this.article.slug, 3)
    },
    relatedByCategory() {
      if (!this.article) return []
      return this.$store.getters['articles/getRelatedByCategory'](this.article.category, this.article.slug, 3)
    },
    latestArticles() {
      return this.$store.getters['articles/getLatestArticles'](3)
    },
    prevNextArticle() {
      if (!this.article) return { prev: null, next: null }
      return this.$store.getters['articles/getPrevNextArticle'](this.article.category, this.article.slug)
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    generateArticleMetaInfo,
    buildBreadcrumbs() {
      if (!this.article) return []
      return buildBreadcrumbs(this.$route, {
        current: this.article.title,
      })
    },
    shareArticle(platform) {
      if (!this.article || typeof window === 'undefined') return
      const articleUrl = `${window.location.origin}/${this.article.category}/${this.article.slug}/`

      if (platform === 'copy') {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(articleUrl)
          return
        }
        window.prompt('Copy this link:', articleUrl) // REDLINE_EXCEPTION: 浏览器兼容 fallback
        return
      }

      const encodedUrl = encodeURIComponent(articleUrl)
      const encodedTitle = encodeURIComponent(this.article.title)
      const targets = {
        x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      }

      if (targets[platform]) {
        window.open(targets[platform], '_blank', 'noopener,noreferrer,width=640,height=640')
      }
    },
  },
  metaInfo() {
    if (!this.article) return {}
    const breadcrumbs = this.buildBreadcrumbs()
    return this.generateArticleMetaInfo(this.article, breadcrumbs)
  },
}
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;
@use 'sass:color';

.article-page {
  position: relative;
  min-height: 100vh;
  padding: rem(40) rem(24);
  overflow: hidden;

  @include pc {
    padding: rem(64) rem(24);
  }

  /* Ambient glow effects */
  &__ambient-glow {
    position: fixed;
    top: -20%;
    left: -10%;
    width: 60%;
    height: 80%;
    background: radial-gradient(circle, rgba($primary-color, 0.12) 0%, rgba(0, 0, 0, 0) 65%);
    filter: blur(80px);
    z-index: 0;
    pointer-events: none;
    animation: pulseGlow 9s infinite alternate;

    &--alt {
      top: 30%;
      left: auto;
      right: -15%;
      width: 50%;
      background: radial-gradient(circle, rgba($secondary-color, 0.08) 0%, rgba(0, 0, 0, 0) 65%);
      animation: pulseGlow 12s infinite alternate-reverse;
    }
  }

  /* Layout wrapper */
  &__wrapper {
    position: relative;
    max-width: 800px;
    margin: 0 auto;
    z-index: 1;
  }

  /* Animated gradient border */
  &__animated-border {
    position: absolute;
    inset: -2px;
    border-radius: $border-radius-lg;
    padding: 2px;
    background: linear-gradient(90deg, $primary-color, $secondary-color, $accent-color, $primary-color);
    background-size: 300% 300%;
    animation: gradientBorder 6s ease infinite;
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity $transition-speed;
    pointer-events: none;

    .article-page__wrapper:hover & {
      opacity: 1;
    }
  }

  /* Glass panel */
  &__glass-panel {
    background: rgba($bg-secondary, 0.7);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: $border-radius-lg;
    padding: rem(32);

    @include pc {
      padding: rem(48);
    }
  }

  /* Breadcrumb */
  &__breadcrumb {
    display: flex;
    align-items: center;
    gap: rem(8);
    margin-bottom: rem(24);
    flex-wrap: wrap;

    a {
      color: $text-secondary;
      text-decoration: none;
      font-size: rem(13);
      transition: color $transition-speed;

      &:hover {
        color: $secondary-color;
      }
    }

    .separator {
      color: $text-muted;
      font-size: rem(13);
    }

    .current {
      color: $text-muted;
      font-size: rem(13);
      @include text-ellipsis(1);
      max-width: rem(300);
    }
  }

  /* Title */
  &__title {
    font-family: $font-family-heading;
    font-size: rem(28);
    font-weight: 800;
    line-height: 1.2;
    color: $text-primary;
    margin-bottom: rem(24);
    letter-spacing: -0.5px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);

    @include pc {
      font-size: rem(42);
      margin-bottom: rem(32);
    }
  }

  /* Meta bar */
  &__meta-bar {
    display: flex;
    align-items: center;
    gap: rem(20);
    margin-bottom: rem(20);
    padding-bottom: rem(20);
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    flex-wrap: wrap;

    @include pc {
      gap: rem(24);
      margin-bottom: rem(24);
    }
  }

  &__share-bar {
    display: flex;
    flex-wrap: wrap;
    gap: rem(10);
    align-items: center;
    margin-bottom: rem(20);
    padding: rem(14) 0 rem(18);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__share-label {
    color: $text-secondary;
    font-size: rem(13);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  &__share-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: rem(36);
    padding: 0 rem(14);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: rem(999);
    background: rgba(255, 255, 255, 0.02);
    color: $text-primary;
    font-size: rem(13);
    font-weight: 600;
    cursor: pointer;
  }

  &__author,
  &__date {
    display: flex;
    align-items: center;
    gap: rem(6);
    color: $text-secondary;
    font-size: rem(14);

    svg {
      opacity: 0.7;
      flex-shrink: 0;
    }
  }

  &__category-badge {
    background: linear-gradient(90deg, $primary-color, color.adjust($primary-color, $lightness: -10%));
    color: #fff;
    font-size: rem(11);
    font-weight: 700;
    padding: rem(4) rem(10);
    border-radius: rem(20);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 4px 12px rgba($primary-color, 0.25);
  }

  /* Tags */
  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: rem(8);
    margin-bottom: rem(32);
  }

  &__tag {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(8px);
    color: $text-secondary;
    font-size: rem(12);
    padding: rem(4) rem(12);
    border-radius: rem(20);
    transition: all $transition-speed;

    &:hover {
      background: rgba($secondary-color, 0.1);
      border-color: rgba($secondary-color, 0.3);
      color: $secondary-color;
    }
  }

  /* Article content */
  &__content {
    line-height: 1.85;
    font-size: rem(16);
    color: $text-primary;

    @include pc {
      font-size: rem(17);
    }

    ::v-deep p {
      margin-bottom: rem(20);
    }

    ::v-deep h2 {
      font-size: rem(24);
      font-weight: 700;
      margin-top: rem(40);
      margin-bottom: rem(16);
      color: $text-primary;
      letter-spacing: -0.3px;
    }

    ::v-deep h3 {
      font-size: rem(20);
      font-weight: 700;
      margin-top: rem(32);
      margin-bottom: rem(12);
      color: $text-primary;
    }

    ::v-deep a {
      color: $primary-color;
      text-decoration: none;
      border-bottom: 1px solid rgba($primary-color, 0.3);
      transition: all $transition-speed;

      &:hover {
        color: $secondary-color;
        border-color: rgba($secondary-color, 0.5);
        text-shadow: 0 0 8px rgba($secondary-color, 0.3);
      }
    }

    ::v-deep img {
      max-width: 100%;
      border-radius: $border-radius;
      margin: rem(24) 0;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
    }

    ::v-deep blockquote {
      background: rgba($primary-color, 0.06);
      border-left: 3px solid $primary-color;
      margin: rem(24) 0;
      padding: rem(16) rem(20);
      border-radius: 0 $border-radius $border-radius 0;

      p {
        margin-bottom: 0;
        color: $text-secondary;
        font-style: italic;
      }
    }

    ::v-deep ul,
    ::v-deep ol {
      padding-left: rem(24);
      margin-bottom: rem(20);

      li {
        margin-bottom: rem(8);
        color: $text-primary;
      }
    }

    ::v-deep code {
      background: rgba(255, 255, 255, 0.08);
      padding: rem(2) rem(6);
      border-radius: rem(4);
      font-size: rem(14);
      color: $secondary-color;
    }

    ::v-deep pre {
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.06);
      border-radius: $border-radius;
      padding: rem(20);
      overflow-x: auto;
      margin: rem(24) 0;

      code {
        background: none;
        padding: 0;
        color: $text-primary;
      }
    }
  }

  /* Not found state */
  &__not-found {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    z-index: 1;
  }

  &__not-found-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60%;
    height: 60%;
    background: radial-gradient(circle, rgba($primary-color, 0.1) 0%, rgba(0, 0, 0, 0) 70%);
    filter: blur(60px);
    pointer-events: none;
  }

  &__not-found-panel {
    background: rgba($bg-secondary, 0.8);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: $border-radius-lg;
    padding: rem(48);
    text-align: center;
    max-width: 480px;

    h1 {
      font-size: rem(28);
      color: $text-primary;
      margin-bottom: rem(16);
    }

    p {
      color: $text-secondary;
      margin-bottom: rem(32);
      line-height: 1.6;
    }
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    gap: rem(8);
    color: $secondary-color;
    text-decoration: none;
    font-weight: 600;
    font-size: rem(14);
    transition: all $transition-speed;

    svg {
      transition: transform $transition-speed;
    }

    &:hover {
      text-shadow: 0 0 12px rgba($secondary-color, 0.4);

      svg {
        transform: translateX(-4px);
      }
    }
  }

  /* Related sections */
  &__related-section {
    margin-top: rem(48);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    z-index: 1;
    position: relative;
  }

  &__section-title {
    font-size: rem(22);
    font-weight: 700;
    color: $text-primary;
    margin: 0 0 rem(24) 0;
    padding-bottom: rem(12);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: rem(20);

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;
    background: rgba($bg-secondary, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: $border-radius;
    overflow: hidden;
    text-decoration: none;
    transition: all $transition-speed;

    &:hover {
      border-color: rgba($primary-color, 0.3);
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    }
  }

  &__card-image {
    width: 100%;
    height: rem(140);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      margin: 0;
      box-shadow: none;
    }
  }

  &__card-body {
    padding: rem(16);
    display: flex;
    flex-direction: column;
    gap: rem(8);
    flex: 1;
  }

  &__card-title {
    font-size: rem(14);
    font-weight: 600;
    color: $text-primary;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__card-date {
    font-size: rem(12);
    color: $text-muted;
    margin-top: auto;
  }

  /* Prev/Next navigation */
  &__prev-next {
    display: flex;
    justify-content: space-between;
    gap: rem(20);
    margin-top: rem(48);
    padding-top: rem(32);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    z-index: 1;
    position: relative;

    @media (max-width: 600px) {
      flex-direction: column;
    }
  }

  &__nav-link {
    display: flex;
    flex-direction: column;
    gap: rem(6);
    padding: rem(16);
    background: rgba($bg-secondary, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: $border-radius;
    text-decoration: none;
    transition: all $transition-speed;
    flex: 1;
    max-width: 48%;

    &:hover {
      border-color: rgba($primary-color, 0.3);
      background: rgba($bg-secondary, 0.8);
    }

    @media (max-width: 600px) {
      max-width: 100%;
    }
  }

  &__nav-link--next {
    text-align: right;
    margin-left: auto;
  }

  &__nav-label {
    font-size: rem(12);
    color: $text-muted;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__nav-title {
    font-size: rem(14);
    font-weight: 600;
    color: $text-primary;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

/* Keyframe animations */
@keyframes pulseGlow {
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.15); opacity: 1; }
}

@keyframes gradientBorder {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
</style>
